export function partialParse(jsonString : string){
    try{
        return JSON.parse(partialComplete(jsonString));
    }catch(e){
        if(e instanceof SyntaxError){
            e.message = "JSON syntax error parsing string: " + jsonString;
        }
        throw e;
    }
}

const States = {
    IN_OBJECT: 0,
    IN_PROP_NAME: 1,
    IN_PROP_NAME_END: 2,
    IN_P_VALUE: 3,
    IN_P_STRING: 4,
    IN_P_NUMBER: 5,
    IN_P_BOOLEAN: 6,
    IN_P_NULL: 7,
    IN_ARRAY: 8,
    IN_A_STRING: 9,
    IN_A_NUMBER: 10,
    IN_A_BOOLEAN: 11,
    IN_A_NULL: 12,
    IN_BETWEEN_PROPS: 13,
    IN_BETWEEN_A_VALUES: 14
}

const Tokens = {
    OPEN_BRACE: '{',
    CLOSE_BRACE: '}',
    OPEN_BRACKET: '[',
    CLOSE_BRACKET: ']',
    COMMA: ',',
    COLON: ':',
    DOUBLE_QUOTE: '"',
    BACK_SLASH: '\\'
}

const getEnumName = (enumObj : object, state : number | string) => {
    for(const [name, value] of Object.entries(enumObj)){
        if(value === state) return name;
    }
}

export function analyseChar(hierarchyStates : number[], currentState : number, char : string, prevChar : string | undefined) : 
{hierarchyStates : number[], currentState : number, trimPoint : boolean, openQuote? : boolean, singleEscapeChar? : boolean}{
    //Are we in a key or value and haven't hit an unescaped double quote then return that we're still in the same state
    if((currentState === States.IN_P_STRING || currentState === States.IN_A_STRING || currentState === States.IN_PROP_NAME) 
        && !(char === Tokens.DOUBLE_QUOTE && prevChar !== Tokens.BACK_SLASH)){
            //It is a trim point if it's in a property. it is not a trim point if it's a single escape character or other state.
        return {
            hierarchyStates,
            currentState,
            trimPoint: ((currentState === States.IN_P_STRING || currentState === States.IN_A_STRING) && !(char === Tokens.BACK_SLASH && prevChar !== Tokens.BACK_SLASH)),
            openQuote: (currentState === States.IN_P_STRING || currentState === States.IN_A_STRING)
        };
    }
    //Otherwise check each type of character and it's meaning
    switch(char){
        case Tokens.OPEN_BRACE:
            hierarchyStates.push(States.IN_OBJECT);
            return {hierarchyStates, currentState: States.IN_OBJECT, trimPoint: true};
        case Tokens.CLOSE_BRACE:
            hierarchyStates.pop();
            return {hierarchyStates, currentState: hierarchyStates[hierarchyStates.length - 1], trimPoint: true}
        case Tokens.OPEN_BRACKET:
            hierarchyStates.push(States.IN_ARRAY);
            return {hierarchyStates, currentState: States.IN_ARRAY, trimPoint: true};
        case Tokens.CLOSE_BRACKET:
            hierarchyStates.pop();
            return {hierarchyStates, currentState: hierarchyStates[hierarchyStates.length - 1], trimPoint: true}
        case Tokens.COMMA:
            if(hierarchyStates[hierarchyStates.length - 1] === States.IN_OBJECT) return {hierarchyStates, currentState: States.IN_BETWEEN_PROPS, trimPoint: false}
            if(hierarchyStates[hierarchyStates.length - 1] === States.IN_ARRAY) return {hierarchyStates, currentState: States.IN_BETWEEN_A_VALUES, trimPoint: false}
            throw new SyntaxError(`Unexpected character ${char} for state ${getEnumName(States,hierarchyStates[hierarchyStates.length - 1])}`);
        case Tokens.COLON:
            if(currentState !== States.IN_PROP_NAME_END) throw new SyntaxError(`Unexpected character ${char} for state ${getEnumName(States,hierarchyStates[hierarchyStates.length - 1])}`);
            return {hierarchyStates, currentState: States.IN_P_VALUE, trimPoint: false};
        case Tokens.DOUBLE_QUOTE:
            switch(currentState){
                case States.IN_OBJECT:
                    return {hierarchyStates, currentState: States.IN_PROP_NAME, trimPoint: false};
                case States.IN_PROP_NAME:
                    return {hierarchyStates, currentState: States.IN_PROP_NAME_END, trimPoint: false};
                case States.IN_BETWEEN_PROPS:
                    return {hierarchyStates, currentState: States.IN_PROP_NAME, trimPoint: false};
                case States.IN_P_VALUE:
                    return {hierarchyStates, currentState: States.IN_P_STRING, trimPoint: true, openQuote: true};
                case States.IN_P_STRING:
                    return {hierarchyStates, currentState: States.IN_OBJECT, trimPoint: true};
                case States.IN_ARRAY:
                    return {hierarchyStates, currentState: States.IN_A_STRING, trimPoint: true, openQuote: true};
                case States.IN_A_STRING:
                    return {hierarchyStates, currentState: States.IN_ARRAY, trimPoint: true};
                case States.IN_BETWEEN_A_VALUES:
                    return {hierarchyStates, currentState: States.IN_A_STRING, trimPoint: true, openQuote: true};
                default:
                    throw new SyntaxError(`Unexpected double quote for state ${getEnumName(States,hierarchyStates[hierarchyStates.length - 1])}`)
            }
        default:
            if(currentState === States.IN_P_VALUE && (char === 't' || char === 'f')) return {hierarchyStates, currentState: States.IN_P_BOOLEAN, trimPoint: false};
            if(currentState === States.IN_P_VALUE && char.match(/[0-9]/)) return {hierarchyStates, currentState: States.IN_P_NUMBER, trimPoint: false};
            if(currentState === States.IN_P_VALUE && char === 'n') return {hierarchyStates, currentState: States.IN_P_NULL, trimPoint: false};
            if(currentState === States.IN_BETWEEN_A_VALUES && (char === 't' || char === 'f')) return {hierarchyStates, currentState: States.IN_A_BOOLEAN, trimPoint: false};
            if(currentState === States.IN_BETWEEN_A_VALUES && char.match(/[0-9]/)) return {hierarchyStates, currentState: States.IN_A_NUMBER, trimPoint: false};
            if(currentState === States.IN_BETWEEN_A_VALUES && char === 'n') return {hierarchyStates, currentState: States.IN_A_NULL, trimPoint: false};
            break;
    }
    return {hierarchyStates, currentState, trimPoint: (currentState === States.IN_P_STRING || currentState === States.IN_A_STRING)};
}

export function establishState(jsonString : string){
    let hierarchyStates : number[] = [];
    let currentState : number = -1;
    let lastChangeIndex : number = 0;
    let lastTrimIndex : number = 0;
    let trimmable : boolean = false;
    let openQuote : boolean = false;
    for(let idx : number = 0; idx < jsonString.length; idx++){
        const res = analyseChar(hierarchyStates, currentState, jsonString[idx], idx === 0 ? undefined : jsonString[idx-1]);
        if(res.currentState !== currentState) lastChangeIndex = idx;
        if(res.trimPoint) lastTrimIndex = idx;
        hierarchyStates = res.hierarchyStates;
        currentState = res.currentState;
        trimmable = res.trimPoint;
        openQuote = !!res.openQuote;
    }
    return {hierarchyStates, lastChangeIndex, lastTrimIndex, trimmable, openQuote, currentState};
}

export function partialComplete(jsonString : string){
    if(!jsonString) throw SyntaxError("String is undefined or null");
    //Remove whitespace at the start and finish
    jsonString = jsonString.trim();
    if(jsonString.length === 0) throw SyntaxError("String is empty");

    //Analyse ending state
    const {hierarchyStates, trimmable, lastTrimIndex, openQuote} = establishState(jsonString);

    if(!trimmable) jsonString = jsonString.slice(0, lastTrimIndex + 1);
    if(!!openQuote) jsonString += "\"";

    hierarchyStates.reverse().forEach((state) => {
        if(state === States.IN_OBJECT) jsonString += '}';
        if(state === States.IN_ARRAY) jsonString += ']';
    });

    return jsonString;
}

export function tokenFinder(jsonString : string){
    let idx = 0;
    let tokens = [];
    while(idx < jsonString.length && tokens.length < 3){
        let res;
        if(idx === 0){
            res = jsonString.slice(-1).match(/[":\{\}\[\],]/);
        }else{
            res = jsonString.slice(-idx-1, -idx).match(/[":\{\}\[\],]/);
        }
        //If the remaining string is only 1 character there is no escape character
        if(res && jsonString.length - idx === 1){
            tokens.push({token: res[0], index: jsonString.length - idx - 1});
        //Otherwise check for a preceding escape character
        }else if(res && !jsonString.slice(-idx-2, -idx-1).match(/\\/)){
            tokens.push({token: res[0], index: jsonString.length - idx - 1});
        }
        idx++;
    }
    return tokens;
}

export function jsonTrimmer(jsonString : string){
    if(!jsonString) throw SyntaxError("String is undefined or null");
    //Remove whitespace at the start and finish
    jsonString = jsonString.trim();
    if(jsonString.length === 0) throw SyntaxError("String is empty");
    //Determine if the last character is an unescaped "ending" token (comma, open or closing brackets/braces), if so, we're good
    if(!jsonString.slice(-1).match(/[\{\}\[\]\,]/) || (jsonString.length !== 1 && jsonString.slice(-2,-1).match(/\\/))){
        //Go and find last 3 non-ending unescaped tokens (colon or double quotation):
        const tokens = tokenFinder(jsonString);
        if(tokens.length === 0) throw new SyntaxError("Didn't find any json tokens");
        //Is the first non-ending unescaped token (colon or double quotation) we encounter a:
        //Quotation - we could be at the start or end of a value or start/end of a property or start or end of an array value
        if(tokens[0].token === '"'){
            //We need to keep looking
            if(tokens.length >=2){
                //If there is a second quotation we have either found a valid value, valid array value, or a property name
                if(tokens[1].token === '"'){
                    //We need to keep looking
                            //If there is a colon - it was a valid value, if there is a square bracket it was a valid array value, if there is a comma or curly brace, it was a property name with no value
                    if(tokens.length >= 3){
                        if(tokens[2].token.match(/[:\[]/)){
                            //Valid value (property or array), no change needed - leaving for clarity
                        }else if(tokens[2].token.match(/[,\{]/)){
                            //Comma or opening curly bracket means it was a property value - we need to cut to that token.
                            jsonString = jsonString.slice(0, tokens[1].index);
                        }
                    }
                //or if it was a colon or opening square bracket, it was a valid value but not closed correctly
                }else if(tokens[1].token.match(/[:\[]/)){
                    jsonString = jsonString + '"';
                //or if it was a comma or curly brace, it was an incomplete property name - remove
                }else if(tokens[1].token.match(/[,\{]/)){
                    jsonString = jsonString.slice(0, tokens[0].index);
                }
            }else{
                throw new SyntaxError("Invalid JSON, only found one double quote and expecting other tokens");
            }
            //or if it was a colon, it was a property name with no value - remove
        }else if(tokens[0].token === ':'){
            jsonString = jsonString.slice(0, tokens[2].index);
        }else{
            throw new SyntaxError("Invalid JSON, expecting a colon or double quotation, found: " + tokens[0].token);
        }
    }
    return jsonString;
}